import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Clock, CheckCheck, LogOut, Loader2, PackageCheck, Truck, Navigation, BellRing, Hand } from "lucide-react";
import { GpsAddress } from "@/components/GpsAddress";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PushToggle } from "@/components/PushToggle";
import { pushEnabled, enablePush } from "@/lib/onesignal";
import { toast } from "sonner";

const ETAS = [10, 20, 30];

function AvailableCard({ order: o, busy, onAccept }) {
  return (
    <motion.div layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
      data-testid={`available-order-${o.order_number}`}
      className="rounded-2xl bg-[#1A1A12] border border-amber-500/50 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="font-mono text-amber-400 font-bold">{o.order_number}</span>
        <span className="text-[10px] font-mono uppercase px-2 py-1 rounded-full bg-amber-500 text-slate-950 font-bold animate-pulse">Nouvelle</span>
      </div>
      <GpsAddress address={o.delivery_address} testid={`available-address-${o.order_number}`} />
      {o.delivery_note && <p className="text-xs text-slate-400 pl-6">📝 {o.delivery_note}</p>}
      <p className="text-xs text-slate-300">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</p>
      <p className="text-sm">{o.payment_method === "cash"
        ? <span className="text-amber-300 font-semibold">Encaisser {o.cash_to_collect.toFixed(2)} € (espèces)</span>
        : <span className="text-emerald-300">Déjà payé (carte) · {o.total.toFixed(2)} €</span>}</p>
      <Button data-testid={`accept-order-${o.order_number}`} disabled={busy} onClick={onAccept} className="w-full btn-gold rounded-xl py-6 text-base">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Hand className="w-5 h-5 mr-2" /> Accepter la commande</>}
      </Button>
    </motion.div>
  );
}

export default function DriverDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [available, setAvailable] = useState([]);
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [arrivalTimes, setArrivalTimes] = useState({});

  const load = useCallback(() => {
    api.get("/driver/orders").then((r) => { setOrders(r.data.mine); setAvailable(r.data.available); setActive(r.data.active); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  // Auto-demande d'autorisation des notifications dès l'arrivée sur la page livreur
  useEffect(() => {
    if (!user?.id || !user?.role || !pushEnabled()) return;
    enablePush(user.role, user.id).catch(() => {});
  }, [user]);

  const doLogout = async () => { await logout(); navigate("/staff"); };

  const setEta = async (order, minutes) => {
    setBusy(order.id + "eta");
    try {
      await api.post(`/driver/orders/${order.id}/eta`, { minutes });
      toast.success(`Client prévenu · Suivi en direct · ETA ${minutes} min`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(null);
  };
  const arrived = async (order) => {
    setBusy(order.id + "arr");
    try { await api.post(`/driver/orders/${order.id}/arrived`); toast.success("Client prévenu · suivi en direct mis à jour"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(null);
  };
  const sendArrivalTime = async (order) => {
    const time = arrivalTimes[order.id];
    if (!time) { toast.error("Choisissez une heure d'arrivée"); return; }
    setBusy(order.id + "time");
    try {
      await api.post(`/driver/orders/${order.id}/arrival_time`, { time });
      toast.success(`Client prévenu · arrivée exacte à ${time}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(null);
  };
  const accept = async (order) => {
    setBusy(order.id + "acc");
    try { await api.post(`/driver/orders/${order.id}/accept`); toast.success(`${order.order_number} acceptée — le client est prévenu`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    load();
    setBusy(null);
  };
  const delivered = async (order) => {
    setBusy(order.id + "del");
    try { await api.post(`/driver/orders/${order.id}/delivered`); toast.success(`${order.order_number} livrée`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(null);
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#0A0D16] pb-10 p-4">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Truck className="w-6 h-6 text-amber-400" />
          <div>
            <h1 className="text-xl font-bold gold-text leading-none">Tournée Livreur</h1>
            <p className="text-[11px] text-slate-400">{user?.name}</p>
          </div>
        </div>
        <button data-testid="driver-logout" onClick={doLogout} className="w-10 h-10 rounded-full glass flex items-center justify-center text-slate-300"><LogOut className="w-4 h-4" /></button>
      </header>

      <div className="glass rounded-xl p-3 mb-3 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
        <p className="text-[11px] text-emerald-300/90">Masquage d'identité activé — le client suit votre progression en direct, sans aucun échange de coordonnées personnelles.</p>
      </div>

      <div className="mb-5"><PushToggle className="w-full justify-center py-3" /></div>

      {!loading && !active && (
        <div data-testid="driver-inactive-banner" className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 mb-5 text-sm text-rose-100">
          Votre compte est désactivé pour le moment : vous ne recevez pas de nouvelles commandes.
        </div>
      )}

      {!loading && available.length > 0 && (
        <div className="mb-6" data-testid="available-orders">
          <p className="text-[11px] font-mono uppercase tracking-widest text-amber-400 mb-2 flex items-center gap-1"><BellRing className="w-3 h-3" /> Nouvelles commandes ({available.length})</p>
          <div className="space-y-3">
            <AnimatePresence>
              {available.map((o) => <AvailableCard key={o.id} order={o} busy={busy === o.id + "acc"} onAccept={() => accept(o)} />)}
            </AnimatePresence>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16">
          <PackageCheck className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">{available.length ? "Acceptez une commande pour démarrer votre course." : "Aucune commande pour le moment."}</p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-[11px] font-mono uppercase tracking-widest text-slate-400">Mes courses ({orders.length})</p>
          <AnimatePresence>
            {orders.map((o) => (
              <motion.div key={o.id} layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
                data-testid={`driver-order-${o.order_number}`}
                className="rounded-2xl bg-[#141827] border border-amber-500/20 overflow-hidden">
                <div className="p-4 border-b border-amber-500/10 flex items-center justify-between">
                  <span className="font-mono text-amber-400 font-bold">{o.order_number}</span>
                  <span className={`text-[10px] font-mono uppercase px-2 py-1 rounded-full ${o.arrived ? "bg-emerald-500/15 text-emerald-300" : (o.eta_minutes || o.exact_arrival_time) ? "bg-amber-500/15 text-amber-300" : "bg-slate-500/15 text-slate-300"}`}>
                    {o.arrived ? "Arrivé" : o.exact_arrival_time ? `Arrivée ${o.exact_arrival_time}` : o.eta_minutes ? `En route · ${o.eta_minutes}min` : "À livrer"}
                  </span>
                </div>
                <div className="p-4 space-y-3">
                  {o.address_changed && (
                    <div data-testid={`address-changed-${o.order_number}`} className="rounded-lg border border-amber-500/60 bg-amber-500/15 px-3 py-2">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-amber-300">⚠ Adresse modifiée par le client</p>
                      {o.previous_address && <p className="text-xs text-slate-500 line-through mt-0.5">{o.previous_address}</p>}
                    </div>
                  )}
                  <GpsAddress address={o.delivery_address} testid={`driver-address-${o.order_number}`} />
                  {o.delivery_note && <p className="text-xs text-slate-400 pl-6">📝 {o.delivery_note}</p>}
                  <div className="bg-[#0F1320] rounded-lg p-3 space-y-1">
                    {o.items.map((i, idx) => (
                      <div key={idx} className="flex justify-between text-sm"><span className="text-slate-300">{i.quantity}× {i.name}</span></div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">
                      {o.payment_method === "cash"
                        ? <span className="text-amber-300 font-semibold">Encaisser {o.cash_to_collect.toFixed(2)} € (espèces)</span>
                        : <span className="text-emerald-300">Déjà payé (carte)</span>}
                    </span>
                  </div>

                  {/* ETA buttons */}
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Temps d'arrivée estimé</p>
                    <div className="grid grid-cols-3 gap-2">
                      {ETAS.map((m) => (
                        <button key={m} data-testid={`eta-button-${m}min-${o.order_number}`} disabled={busy === o.id + "eta"} onClick={() => setEta(o, m)}
                          className={`py-2 rounded-lg text-sm font-mono transition-all ${o.eta_minutes === m && !o.exact_arrival_time ? "btn-gold" : "glass text-slate-200 hover:text-amber-300"}`}>
                          {`${m} min`}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Exact arrival time (after GPS) */}
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><Navigation className="w-3 h-3" /> Heure d'arrivée exacte (après GPS)</p>
                    <div className="flex gap-2">
                      <input type="time" data-testid={`arrival-time-input-${o.order_number}`}
                        value={arrivalTimes[o.id] || ""} onChange={(e) => setArrivalTimes((p) => ({ ...p, [o.id]: e.target.value }))}
                        className="flex-1 bg-[#0F1320] border border-amber-500/25 rounded-lg px-3 py-2 text-slate-100 text-sm font-mono focus:outline-none focus:border-amber-500" />
                      <Button data-testid={`arrival-time-button-${o.order_number}`} disabled={busy === o.id + "time"} onClick={() => sendArrivalTime(o)}
                        className="btn-gold rounded-lg px-4">
                        {busy === o.id + "time" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer"}
                      </Button>
                    </div>
                    {o.exact_arrival_time && (
                      <p className="text-[11px] text-amber-300 mt-1.5">⏰ Heure communiquée : <span className="font-mono font-semibold">{o.exact_arrival_time}</span> — le client doit être dehors à cette heure.</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <Button data-testid={`arrived-button-${o.order_number}`} disabled={busy === o.id + "arr"} onClick={() => arrived(o)}
                      variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-xl">
                      {busy === o.id + "arr" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Je suis arrivé"}
                    </Button>
                    <Button data-testid={`delivered-button-${o.order_number}`} disabled={busy === o.id + "del"} onClick={() => delivered(o)}
                      className="btn-gold rounded-xl">
                      {busy === o.id + "del" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCheck className="w-4 h-4 mr-1" /> Livrée</>}
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
